import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import {
  ArrowRight, ChevronRight, Wrench, HardHat, Zap, Building, Cpu, Stethoscope, Landmark,
  FileText, Search, Scale, ThumbsUp, ShoppingCart, Truck,
} from "lucide-react";
import { RFQForm } from "../components/site/RFQForm";
import { track } from "../lib/track";
import { Seo } from "../components/site/Seo";
import { ServiceAreas } from "../components/site/ServiceAreas";
import { FederalHighlight } from "./FederalPages";
import {
  ValueProp, WhoWeServe, OperationsGallery, About, MedicalSubsidiary, ContactSection, ClientExpectations,
} from "../components/LandingPage";

const HERO_INDUSTRIES = ["Industrial", "Energy", "Government", "Construction", "Manufacturing", "Healthcare"];

function Hero() {
  const goContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="hero" data-testid="hero-section" className="relative pt-[140px] pb-20 md:pb-24 overflow-hidden hero-bg">
      <div className="pg-container relative">
        <div className="max-w-4xl fade-up">
          <span className="eyebrow" data-testid="hero-eyebrow" style={{ color: "#7EC4FF" }}>
            Industrial Procurement • Government Solutions • Strategic Sourcing • Logistics
          </span>
          <h1 className="mt-5 text-[40px] md:text-[56px] lg:text-[64px] font-extrabold leading-[1.04] text-white">
            We Source. <span className="text-[var(--pg-accent)]">We Procure.</span> We Deliver.
          </h1>
          <p className="mt-6 text-[17px] md:text-[19px] text-white/80 max-w-3xl leading-relaxed">
            Patrick Global helps businesses, government organizations, contractors, and institutions
            source products, equipment, and supplies from qualified suppliers while simplifying
            procurement, controlling costs, and coordinating delivery.
          </p>
          <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-cta-row">
            <Link data-testid="hero-primary-cta" to="/request-a-quote" className="btn-primary"
              onClick={() => track("submit_rfq_clicked", { source: "hero" })}>
              Submit an RFQ <ArrowRight size={18} />
            </Link>
            <button data-testid="hero-secondary-cta" onClick={goContact} className="btn-ghost-on-dark">
              Talk to a Procurement Specialist <ChevronRight size={18} />
            </button>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-white/15 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="hero-industry-strip">
          {HERO_INDUSTRIES.map((t) => (
            <span key={t} className="font-[Poppins] font-semibold text-[13.5px] tracking-[0.14em] uppercase text-white/70 flex items-center gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--pg-accent)]"></span>{t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function RFQModule() {
  return (
    <section id="rfq" data-testid="home-rfq-section" className="py-20 md:py-24 bg-[var(--pg-bg)]">
      <div className="pg-container grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <span className="eyebrow">Start Here</span>
          <h2 className="mt-5 text-[34px] md:text-[42px] font-extrabold leading-[1.08]">Tell Us What You Need</h2>
          <p className="mt-5 text-[16.5px] text-[var(--pg-ink-soft)] leading-relaxed">
            Don't spend hours searching for suppliers. Send us your RFQ, purchase list,
            specifications, drawings — or simply tell us what you need.
          </p>
          <ul className="mt-7 space-y-3">
            {["Qualified supplier sourcing", "Competitive quotes & landed-cost analysis", "One point of contact from RFQ to delivery"].map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15px] text-[var(--pg-navy)] font-medium">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--pg-accent)] flex-shrink-0"></span>{b}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-8">
          <RFQForm compact />
        </div>
      </div>
    </section>
  );
}

const SOURCE_CATEGORIES = [
  { icon: Wrench, title: "Industrial & MRO", body: "Valves • Pumps • Motors • Bearings • Filters • Gaskets • Hoses • Fittings • Fasteners • Industrial Components" },
  { icon: HardHat, title: "Safety & PPE", body: "Hard Hats • Safety Glasses • Gloves • FR Clothing • Fall Protection • Respiratory Protection • Safety Equipment" },
  { icon: Zap, title: "Electrical & Equipment", body: "Electrical Components • Cable • Switchgear • Transformers • Control Panels • Industrial Lighting • Generators" },
  { icon: Building, title: "Construction & Facility Supplies", body: "Construction Materials • Tools • Equipment • Maintenance Supplies • Facility Products • Jobsite Supplies" },
  { icon: Cpu, title: "Technology", body: "IT Hardware • Networking Equipment • Computers • Peripherals • Technology Equipment" },
  { icon: Stethoscope, title: "Healthcare", body: "Medical Supplies • Healthcare Equipment • Medical Logistics • Procurement Support" },
  { icon: Landmark, title: "Government", body: "Government Supplies • Equipment • Industrial Products • Facility Supplies • Logistics Solutions" },
];

function WhatWeSource() {
  return (
    <section data-testid="what-we-source-section" className="py-20 md:py-28 bg-white">
      <div className="pg-container">
        <div className="max-w-3xl">
          <span className="eyebrow">What We Source</span>
          <h2 className="mt-5 text-[34px] md:text-[44px] font-extrabold leading-[1.08]">
            What Can Patrick Global Source for You?
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {SOURCE_CATEGORIES.map((c, i) => (
            <div key={c.title} data-testid={`source-category-${i}`} className="lift-card bg-white border border-slate-200 rounded-2xl p-6 flex flex-col">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #0B1F3A 0%, #1E90FF 100%)" }}>
                <c.icon size={20} />
              </div>
              <h3 className="mt-4 font-[Poppins] font-bold text-[17px]">{c.title}</h3>
              <p className="mt-2 text-[13.5px] text-[var(--pg-ink-soft)] leading-relaxed flex-1">{c.body}</p>
            </div>
          ))}
          <Link to="/request-a-quote" data-testid="source-category-cta"
            className="rounded-2xl p-6 flex flex-col justify-center items-start text-white bg-gradient-to-br from-[var(--pg-navy)] to-[var(--pg-navy-2)] lift-card">
            <h3 className="font-[Poppins] font-bold text-[18px] leading-snug">Don't see what you need?</h3>
            <p className="mt-2 text-[13.5px] text-white/80">Send us the requirement — we'll source it.</p>
            <span className="mt-5 inline-flex items-center gap-2 text-[13.5px] font-[Poppins] font-semibold text-[var(--pg-accent)]">
              Submit an RFQ <ArrowRight size={15} />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: FileText, num: "01", title: "Tell Us What You Need", body: "Send your RFQ, product list, specifications, drawings, or procurement requirement." },
  { icon: Search, num: "02", title: "We Source", body: "Our procurement team identifies qualified suppliers and sourcing options." },
  { icon: Scale, num: "03", title: "We Compare", body: "We evaluate price, quality, availability, lead time, and logistics." },
  { icon: ThumbsUp, num: "04", title: "You Approve", body: "Receive a clear procurement proposal with transparent pricing." },
  { icon: ShoppingCart, num: "05", title: "We Procure", body: "Patrick Global coordinates purchasing and supplier execution." },
  { icon: Truck, num: "06", title: "We Deliver", body: "We coordinate logistics and delivery according to agreed requirements." },
];

function HowItWorks() {
  return (
    <section data-testid="how-it-works-section" className="py-20 md:py-28 dark-section">
      <div className="pg-container">
        <div className="max-w-3xl">
          <span className="eyebrow" style={{ color: "#7EC4FF" }}>How It Works</span>
          <h2 className="mt-5 text-[34px] md:text-[44px] font-extrabold leading-[1.08] text-white">
            From Requirement to Delivery
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.num} data-testid={`how-it-works-step-${i}`} className="bg-white/[0.04] border border-white/10 hover:border-[var(--pg-accent)]/60 rounded-2xl p-7 transition-colors">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-lg bg-[var(--pg-accent)] text-white flex items-center justify-center"><s.icon size={20} /></div>
                <span className="font-[Poppins] font-extrabold text-[28px] text-white/15">{s.num}</span>
              </div>
              <h3 className="mt-5 text-white font-[Poppins] font-semibold text-[18px]">{s.title}</h3>
              <p className="mt-2 text-white/75 text-[14px] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/request-a-quote" data-testid="how-it-works-cta" className="btn-primary"
            onClick={() => track("submit_rfq_clicked", { source: "how_it_works" })}>
            Submit an RFQ <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function SourcingCTA() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        track("cta_viewed", { section: "sourcing_cta" }, true);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} data-testid="sourcing-cta-section" aria-labelledby="sourcing-cta-heading"
      className="pt-4 pb-16 md:pt-6 md:pb-20 bg-[var(--pg-bg)]">
      <div className="pg-container">
        <div className="rounded-3xl bg-gradient-to-br from-[var(--pg-navy)] to-[var(--pg-navy-2)] border border-white/10 px-7 py-12 md:px-14 md:py-16">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="eyebrow" style={{ color: "#7EC4FF" }}>Strategic Sourcing &amp; Procurement</span>
              <h2 id="sourcing-cta-heading" className="mt-4 text-[32px] md:text-[42px] font-extrabold leading-[1.08] text-white">
                Need Something Sourced?
              </h2>
              <p className="mt-4 text-[16px] md:text-[17px] text-white/80 leading-relaxed max-w-2xl">
                Send us your requirement. We'll find qualified suppliers, compare competitive options,
                and manage the procurement process from RFQ to delivery — equipment, materials, and supplies.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-stretch">
              <Link to="/request-a-quote" data-testid="sourcing-cta-primary" className="btn-orange-accent justify-center text-center"
                onClick={() => track("submit_rfq_clicked", { source: "sourcing_cta" })}>
                Submit an RFQ <ArrowRight size={18} />
              </Link>
              <button data-testid="sourcing-cta-secondary" className="btn-ghost-on-dark justify-center text-center"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>
                Talk to a Procurement Specialist <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const goContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div data-testid="home-page">
      <Seo route="/" />
      <Hero />
      <FederalHighlight />
      <RFQModule />
      <WhatWeSource />
      <HowItWorks />
      <ValueProp />
      <WhoWeServe />
      <ServiceAreas compact />
      <OperationsGallery />
      <About />
      <ClientExpectations />
      <SourcingCTA />
      <MedicalSubsidiary onContactClick={goContact} />
      <ContactSection />
    </div>
  );
}
